"use client";

/**
 * PDF Viewer Component
 *
 * Uses pdfjs-dist to render PDF documents with citation text highlighting.
 *
 * ## PDF.js Worker Configuration
 *
 * The PDF.js library requires a web worker for parsing PDFs. The worker file
 * is kept in sync with the pdfjs-dist version automatically:
 *
 * 1. `postinstall` script copies the worker after `npm install`
 * 2. `prebuild` script copies the worker before each build
 *
 * This ensures the worker version always matches the installed pdfjs-dist.
 *
 * ### When updating pdfjs-dist:
 * - Run `npm install` (postinstall handles the copy)
 * - Or run `npm run build` (prebuild handles the copy)
 * - The worker at `/public/pdf.worker.min.mjs` will be updated automatically
 *
 * @see package.json for the postinstall and prebuild scripts
 */

import React from "react";
// PINNED to 5.4.624 — do not upgrade past 5.4.x until Map.getOrInsertComputed
// has broad browser support. pdfjs-dist 5.5.207+ uses this TC39 Stage 4 API
// which is missing in Safari, Firefox, and some Chromium builds. Upgrading
// causes silent render failures (blank canvas, no error in UI).
// Revisit after Safari 19+ and Firefox 140+ ship with support (~Q3 2026).
import * as pdfjsLib from "pdfjs-dist";
import { Button, Spinner, Tooltip, TooltipTrigger, TooltipContent } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { PDFDocumentProxy, TextItem } from "pdfjs-dist/types/src/display/api";

import { MatchResult } from "@/utils/textMatching";
import { logger } from "@/telemetry";
import { PdfCitationBox } from "@/types/citations";

import { matchCitationText, planFromAnchor, planFromTextMatch } from "./pdfCitationPlan";
import { applyCitationView } from "./pdfCitationView";

// Configure PDF.js worker for version 5.x
// The worker file is copied to public/ folder via postinstall/prebuild scripts
// This avoids external CDN dependency and ensures version compatibility
if (typeof window !== "undefined") {
  pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";
}

/**
 * Scale the text layer coordinates are baked at.
 *
 * Highlight rectangles are derived from `textLayer`, whose x, y, width and height
 * are multiplied by this figure once during extraction. Rather than re-running the
 * text matching whenever the zoom changes, the highlight overlay is sized at this
 * scale and then transformed to the live scale, so the two stay registered against
 * the canvas at any zoom.
 *
 * Extraction and the overlay are a matched pair, being the sizing and the transform
 * together, and changing how one of them uses this figure without the other silently
 * unregisters every highlight from the page beneath it. A stored citation anchor
 * joins the pair on the extraction side. Its boxes arrive in PDF points at scale 1
 * and are lifted by this figure once, in `planFromAnchor` (`./pdfCitationPlan`), which
 * the highlight-planning effect hands this figure, so they land in the same units
 * `textLayer` is baked in. The citation scroll (`scrollToBox` in `./pdfCitationView`)
 * reads the same pair from the outside, multiplying a planned box by
 * `scale / REFERENCE_SCALE`, passed to it as `overlayScale`, to learn
 * where that box is painted, so it has to move with the overlay's transform or the
 * reader is sent somewhere the highlight is not. The one reference that stands apart
 * is the fallback in `scale` below, which is about a container that cannot be measured
 * rather than about the text layer.
 */
const REFERENCE_SCALE = 1.5;

/** Closest and furthest the manual zoom controls will go, as a canvas scale factor. */
const MIN_SCALE = 0.25;
const MAX_SCALE = 4;

/** Added or removed by one press of zoom in or zoom out. */
const SCALE_STEP = 0.25;

/** Turns a fractional scale (1.25) into a whole percentage for display (125). */
const ZOOM_PERCENTAGE_MULTIPLIER = 100;

/**
 * Horizontal padding inside the scroll container, being `p-4` on both sides, which
 * fit-to-width has to leave room for or the fitted page overflows by that amount and
 * reintroduces the horizontal scrollbar it exists to avoid.
 */
const CONTAINER_PADDING_PX = 32;

/**
 * How long a text-matched citation waits before scrolling and painting, which is the
 * fast path for a short document. `renderVersion` is what guarantees a redraw on a
 * long one. A measured citation does not wait at all.
 */
const TEXT_MATCH_SETTLE_DELAY_MS = 300;

/**
 * pdfjs reports a superseded or aborted render by name rather than by error type, so
 * the name is all there is to match on.
 */
const isRenderCancellation = (error: unknown): boolean => {
  const name = (error as { name?: string } | null | undefined)?.name;

  return name === "RenderingCancelledException" || name === "AbortException";
};

/** Keeps a scale inside the bounds the controls offer. */
const clampScale = (value: number): number => Math.min(MAX_SCALE, Math.max(MIN_SCALE, value));

export interface PDFViewerProps {
  /** PDF document as ArrayBuffer */
  documentData: ArrayBuffer;

  /** Text to find and highlight in the PDF */
  citationText?: string;

  /**
   * Where the cited passage sits, measured at ingestion, in PDF points with a
   * top-left origin. See `PdfCitationBox` for the coordinate contract and its
   * one known limitation.
   *
   * When present, the highlight-planning effect uses these and never runs the
   * text search, because the search is the thing going wrong. The rationale is
   * in the header of `@/utils/citationAnchor`.
   *
   * Absent for every document ingested before anchors existed and for every
   * chat upload, so it is optional and its absence is the ordinary case rather
   * than a fault.
   */
  citationBoxes?: PdfCitationBox[];

  /**
   * Callback when text is found or not found.
   *
   * Fires only when the text search runs. When `citationBoxes` is supplied the
   * search is skipped and this is never called, since there is no match result
   * to report.
   */
  onMatchResult?: (result: MatchResult | null) => void;
}

export type { PdfCitationBox } from "@/types/citations";

interface TextLayerItem {
  text: string;
  transform: number[];
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * PDF Viewer component using PDF.js
 * Renders PDF pages and highlights citation text
 */
export const PDFViewer: React.FC<PDFViewerProps> = ({
  documentData,
  citationText,
  citationBoxes,
  onMatchResult,
}) => {
  const { t } = useTranslation();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [pdfDocument, setPdfDocument] = React.useState<PDFDocumentProxy | null>(null);
  const [numPages, setNumPages] = React.useState(0);
  /** Live width of the scroll container, tracked so fit-to-width survives a resize. */
  const [containerWidth, setContainerWidth] = React.useState(0);
  /**
   * Whether the container has been measured at all, as distinct from having been
   * measured as zero. The render pass waits on this rather than on a usable width, so
   * a container that cannot be measured still renders at the fallback scale.
   */
  const [hasMeasured, setHasMeasured] = React.useState(false);
  /**
   * Width in points of the widest page in the document, measured at scale 1. The
   * widest rather than the first, so that a landscape page in an otherwise portrait
   * document does not overflow once the rest has been fitted.
   */
  const [widestPageWidth, setWidestPageWidth] = React.useState(0);
  /**
   * A scale the user chose explicitly, which pins the zoom. Null means the zoom
   * follows fit-to-width, which is the default and what a resize recalculates.
   */
  const [userScale, setUserScale] = React.useState<number | null>(null);
  /**
   * Page the view has already been scrolled to. Consulted so that re-running the
   * highlight effect after a zoom redraws the overlay without yanking the view back
   * to the citation the user may have scrolled away from.
   */
  const lastScrolledPageRef = React.useRef<number | null>(null);
  /**
   * Whether the highlight currently planned came from stored coordinates rather
   * than from searching the text.
   *
   * A ref rather than state because nothing renders from it. It is written in
   * the highlight-planning effect and read in the scroll effect within the same
   * commit, and only decides the scroll behaviour, so a state write would
   * re-render the toolbar for no visible change.
   */
  const anchorPlanRef = React.useRef(false);
  const [textLayer, setTextLayer] = React.useState<TextLayerItem[]>([]);
  const [matchedPage, setMatchedPage] = React.useState<number | null>(null);
  const canvasRefs = React.useRef<Map<number, HTMLCanvasElement>>(new Map());
  const highlightRefs = React.useRef<Map<number, HTMLDivElement>>(new Map());
  /**
   * Incremented once every page has finished rendering. The highlight effect depends
   * on it so that it redraws after the pages it draws onto actually exist.
   *
   * Pages render sequentially, each awaiting the previous page's raster, so on a long
   * document the fixed 300ms delay below expires long before the matched page has been
   * appended. The effect then found no element, logged that pages were still rendering
   * and never retried, so a citation past the first few pages lost its highlight on
   * every zoom press. The delay is kept as the fast path for short documents, and this
   * counter is the one that guarantees a redraw on long ones.
   */
  const [renderVersion, setRenderVersion] = React.useState(0);
  const [pageHighlights, setPageHighlights] = React.useState<
    Record<number, Array<{ left: number; top: number; width: number; height: number }>>
  >({});

  /**
   * Scale at which the widest page exactly fills the available width, or zero where
   * either measurement is still missing.
   */
  const fitScale =
    containerWidth > 0 && widestPageWidth > 0
      ? clampScale((containerWidth - CONTAINER_PADDING_PX) / widestPageWidth)
      : 0;

  /**
   * The scale actually rendered, being the user's pinned choice where they have made
   * one, then fit-to-width, then REFERENCE_SCALE. That last fallback matters, because
   * a container whose width cannot be read yields a fit scale of zero, and a viewer
   * that renders nothing at all would be a worse failure than one rendering unfitted.
   */
  const scale = userScale ?? (fitScale > 0 ? fitScale : REFERENCE_SCALE);

  /** True while the zoom is following the container rather than a pinned choice. */
  const isFitToWidth = userScale === null;

  /**
   * Track the scroll container's width so fit-to-width responds to the modal being
   * resized, the window changing, or the sidebar opening.
   *
   * A layout effect rather than a passive one, so the width is known before the
   * browser paints and pages are drawn once at the fitted scale instead of appearing
   * at REFERENCE_SCALE and jumping. `hasMeasured` is what the render effect waits on,
   * being set even when the width reads as zero, so an unmeasurable container still
   * renders at the fallback rather than staying blank.
   *
   * Keyed on `pdfDocument` because the container is only mounted once loading has
   * finished, so an effect running on mount alone would find no element to observe.
   */
  React.useLayoutEffect(() => {
    const element = scrollRef.current;

    if (!element) return;

    setContainerWidth(element.clientWidth);
    setHasMeasured(true);

    if (typeof ResizeObserver === "undefined") {
      // The measurement above has already run, so fit-to-width still works and only
      // its response to a resize is lost. Logged because that degradation is otherwise
      // invisible, and would be reported as "fit to width is broken" with nothing in
      // telemetry to point at.
      logger.warn("ResizeObserver unavailable, PDF fit-to-width will not follow resizes");

      return;
    }

    // `clientWidth` rather than the entry's `contentRect`, so both this and the
    // initial measurement above report the same box. `contentRect` is the content
    // box and has already excluded the container's padding, so feeding it through
    // the same `CONTAINER_PADDING_PX` subtraction deducted that padding a second
    // time and left every page fitted 32px narrower than the space available.
    // `clientWidth` is also what every other measurement site in the app reads.
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth((entry.target as HTMLElement).clientWidth);
      }
    });

    observer.observe(element);

    return () => observer.disconnect();
  }, [pdfDocument]);

  // Both handlers step from `scale`, the figure actually rendered, rather than from
  // `fitScale`. The two agree while fit-to-width is working, but `fitScale` is zero
  // where the container could not be measured and `scale` has fallen back to
  // REFERENCE_SCALE, and stepping from zero there clamped to MIN_SCALE in either
  // direction, so zoom in dropped the view from 150% to 25% and zoom out produced
  // that same value.
  const handleZoomIn = React.useCallback(() => {
    setUserScale((current) => clampScale((current ?? scale) + SCALE_STEP));
  }, [scale]);

  const handleZoomOut = React.useCallback(() => {
    setUserScale((current) => clampScale((current ?? scale) - SCALE_STEP));
  }, [scale]);

  const handleFitToWidth = React.useCallback(() => {
    setUserScale(null);
  }, []);

  // Load PDF document
  React.useEffect(() => {
    let isMounted = true;
    let loadingTask: ReturnType<typeof pdfjsLib.getDocument> | null = null;

    const loadPDF = async () => {
      try {
        setLoading(true);
        setError(null);
        // A different document opens fitted to the container and unscrolled, rather
        // than inheriting the zoom and scroll position of the one before it.
        setUserScale(null);
        setWidestPageWidth(0);
        lastScrolledPageRef.current = null;

        loadingTask = pdfjsLib.getDocument({ data: documentData });

        const pdf = await loadingTask.promise;

        if (!isMounted) return;

        // Measure every page at scale 1 so fit-to-width can size against the widest of
        // them rather than the first, which is what stops a landscape page in an
        // otherwise portrait document overflowing once the rest has been fitted.
        // `getPage` caches inside pdfjs, so the render and text extraction passes that
        // follow reuse these pages rather than fetching each one again. Measuring is
        // concurrent because each page is an independent worker round trip, and it has
        // to finish before first paint, or pages would be drawn at the fallback scale
        // and then jump once the fitted scale arrived.
        //
        // `allSettled` rather than `all`, because `all` rejects on the first rejection
        // and sent a single malformed page dictionary to the outer catch, replacing a
        // perfectly readable report with "Failed to load PDF document". A page that
        // cannot be measured now costs only its own contribution to the widest figure,
        // and a document where none can be measured falls back to REFERENCE_SCALE.
        const pageNumbers = Array.from({ length: pdf.numPages }, (_unused, index) => index + 1);
        let cropOffsetReported = false;
        const measurements = await Promise.allSettled(
          pageNumbers.map(async (pageNum) => {
            const page = await pdf.getPage(pageNum);

            // A stored citation anchor is measured against the MediaBox, while the
            // overlay is sized from the CropBox, so a page cropped to an offset
            // origin draws its highlight shifted by that offset. Nothing here can
            // correct it, because pdf.js exposes only the cropped view, so the
            // case is made visible instead of failing silently.
            const view = page.view;

            if (!cropOffsetReported && Array.isArray(view) && (view[0] !== 0 || view[1] !== 0)) {
              cropOffsetReported = true;
              logger.warn("PDF page has an offset CropBox; stored highlights may be shifted", {
                pageNum,
                viewLeft: view[0],
                viewBottom: view[1],
              });
            }

            return page.getViewport({ scale: 1 }).width;
          }),
        );
        const unmeasurable = pageNumbers.filter(
          (_unused, index) => measurements[index].status === "rejected",
        );

        if (unmeasurable.length > 0) {
          const firstRejection = measurements.find(
            (result): result is PromiseRejectedResult => result.status === "rejected",
          );

          logger.error("Failed to measure some PDF pages", firstRejection?.reason, {
            failedPages: unmeasurable.join(","),
            totalPages: pdf.numPages,
          });
        }

        const widest = measurements.reduce(
          (max, result) => (result.status === "fulfilled" ? Math.max(max, result.value) : max),
          0,
        );

        if (!isMounted) return;

        setPdfDocument(pdf);
        setNumPages(pdf.numPages);
        setWidestPageWidth(widest);
        logger.info("📄 PDF loaded", { numPages: pdf.numPages, widestPageWidth: widest });
      } catch (err) {
        logger.error("Failed to load PDF", err);
        if (isMounted) {
          setError(t("citations.pdfViewer.loadError"));
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadPDF();

    return () => {
      isMounted = false;
      // Release the worker port and the page cache. Nothing destroyed the document
      // before, so each citation opened in a session left both behind, and because the
      // measurement above pulls every page into that cache the residue is now the
      // whole document rather than only the pages the reader reached. Renders still in
      // flight reject with an abort, which the render loop logs at debug.
      loadingTask?.destroy().catch((err) => {
        logger.error("Failed to destroy PDF loading task", err);
      });
    };
    // `t` is deliberately omitted. It is stable in production, but the global
    // `useTranslation` mock in src/setupTests.ts returns a fresh function on every
    // render, so depending on it here reloads the document in a loop and takes the
    // vitest worker down with it. See the testing notes in CLAUDE.md.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentData]);

  // Extract text layer from all pages
  React.useEffect(() => {
    if (!pdfDocument) return;

    let isMounted = true;

    const extractTextLayer = async () => {
      try {
        const items: TextLayerItem[] = [];

        // IMPORTANT: these coordinates are baked at REFERENCE_SCALE, not at the live
        // zoom. The overlay that draws them is transformed from this scale to the
        // rendered one, so extraction never has to re-run when the user zooms.
        const scale = REFERENCE_SCALE;

        for (let pageNum = 1; pageNum <= pdfDocument.numPages; pageNum++) {
          const page = await pdfDocument.getPage(pageNum);
          const textContent = await page.getTextContent();
          const viewport = page.getViewport({ scale });

          textContent.items.forEach((item) => {
            if ("str" in item) {
              const textItem = item as TextItem;
              const transform = textItem.transform;

              // Calculate position from transform matrix
              // transform is [scaleX, skewY, skewX, scaleY, translateX, translateY]
              // These coordinates are at the same scale as the canvas
              const x = transform[4] * scale;
              const y = viewport.height - transform[5] * scale; // Flip Y coordinate
              const width = textItem.width * scale;
              const height = textItem.height * scale;

              items.push({
                text: textItem.str,
                transform,
                page: pageNum,
                x,
                y,
                width,
                height,
              });
            }
          });
        }

        if (isMounted) {
          setTextLayer(items);
          logger.info("📝 Extracted text from PDF pages", {
            numPages: pdfDocument.numPages,
            itemCount: items.length,
          });
        }
      } catch (err) {
        logger.error("Failed to extract text layer", err);
      }
    };

    extractTextLayer();

    return () => {
      isMounted = false;
    };
  }, [pdfDocument]);

  // Plan where the citation's highlight goes. The steps are in `./pdfCitationPlan`.
  React.useEffect(() => {
    // A measured anchor beats any search, so take it and stop.
    //
    // Deliberately ahead of the `textLayer.length` guard below, because this
    // path needs no text layer at all: the rectangles are already known, so the
    // highlight can be planned as soon as the citation arrives rather than
    // waiting on every page to be extracted.
    //
    // The scale conversion is the one coupling to keep. `pageHighlights` is
    // consumed by the painting effect in the same units `textLayer` is baked
    // in, which is REFERENCE_SCALE rather than the live `scale`; the overlay is
    // sized at that reference and transformed onto the canvas on zoom. Stored
    // points multiplied by anything else would be right at one zoom level only.
    // When no box lands on a page the document has, this falls through to the
    // search.
    const anchorPlan = planFromAnchor(citationBoxes, numPages, REFERENCE_SCALE);

    if (anchorPlan) {
      anchorPlanRef.current = true;
      setPageHighlights(anchorPlan.plan);
      setMatchedPage(anchorPlan.matchedPage);

      return;
    }

    // Reaching here means this citation has no measured position, so the view
    // must not keep the previous citation's mode. The modal unmounts this viewer
    // when it closes, so the reset is defensive rather than load-bearing today.
    // It matters only if the citation changes while the modal stays open and the
    // next document is already cached, when a stale true would make an unmeasured
    // citation jump instantly to a page found only after a full extraction.
    anchorPlanRef.current = false;

    if (!citationText || textLayer.length === 0) return;

    const matchResult = matchCitationText(textLayer, citationText);

    if (onMatchResult) {
      onMatchResult(matchResult);
    }

    // Find start and end across pages and compute cross-page highlights
    if (!matchResult) return;

    const textPlan = planFromTextMatch(textLayer, citationText, matchResult);

    if (!textPlan) return;

    setPageHighlights(textPlan.plan);
    setMatchedPage(textPlan.matchedPage);
    logger.info("🎨 Cross-page highlight planned", {
      fromPage: textPlan.fromPage,
      toPage: textPlan.toPage,
      totalBoxes: textPlan.totalBoxes,
    });
  }, [citationBoxes, citationText, textLayer, onMatchResult, numPages]);

  // Render PDF pages. Re-runs on a zoom, because the canvas is a raster and scaling
  // it in CSS would blur the text rather than sharpen it.
  React.useEffect(() => {
    if (!pdfDocument || !containerRef.current) return;

    // Wait for the container to have been measured before drawing anything, so pages
    // are not rendered at the fallback scale and immediately redrawn at the fitted one.
    if (!hasMeasured) return;

    let isMounted = true;
    const renderTasks: Array<{ cancel: () => void }> = [];

    const renderPages = async () => {
      const container = containerRef.current;

      if (!container) return;

      // Clear existing canvases
      container.innerHTML = "";
      canvasRefs.current.clear();
      highlightRefs.current.clear();

      for (let pageNum = 1; pageNum <= pdfDocument.numPages; pageNum++) {
        if (!isMounted) return;

        try {
          const page = await pdfDocument.getPage(pageNum);
          const viewport = page.getViewport({ scale });

          // Create page container
          const pageDiv = document.createElement("div");

          pageDiv.className = "relative mb-4 shadow-medium";
          pageDiv.style.width = `${viewport.width}px`;
          pageDiv.style.height = `${viewport.height}px`;

          // Create canvas for PDF rendering
          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");

          if (!context) {
            // The page still has to join the DOM. `container.children` is indexed
            // positionally by the scroll target below, so omitting the append shifted
            // every later page down one place and sent a citation to the wrong page
            // while highlighting the right one, with the two disagreeing silently. A
            // browser returns null here once its canvas memory ceiling is passed,
            // which the 400% zoom ceiling puts within reach on a long document.
            logger.error("Could not acquire a 2D context for a PDF page", undefined, {
              pageNum,
              scale,
              totalPages: pdfDocument.numPages,
            });
            container.appendChild(pageDiv);
            continue;
          }

          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.className = "block";

          // Create highlight layer. It is sized at REFERENCE_SCALE, because that is
          // the scale the rectangles in `textLayer` and `pageHighlights` were computed
          // at, and then transformed onto the live canvas. Transforming the whole
          // overlay is exact and keeps the text matching independent of the zoom,
          // where re-deriving every rectangle per zoom step would not be.
          const referenceViewport = page.getViewport({ scale: REFERENCE_SCALE });
          const highlightLayer = document.createElement("div");

          // `overflow-hidden` because the anchor path draws rectangles this file did
          // not derive, so a mis-scaled or stale box must not bleed onto the page
          // below. It does not correct a rectangle misplaced within its own page.
          highlightLayer.className = "absolute top-0 left-0 pointer-events-none overflow-hidden";
          highlightLayer.style.width = `${referenceViewport.width}px`;
          highlightLayer.style.height = `${referenceViewport.height}px`;
          highlightLayer.style.transformOrigin = "top left";
          highlightLayer.style.transform = `scale(${scale / REFERENCE_SCALE})`;

          pageDiv.appendChild(canvas);
          pageDiv.appendChild(highlightLayer);
          container.appendChild(pageDiv);

          canvasRefs.current.set(pageNum, canvas);
          highlightRefs.current.set(pageNum, highlightLayer);

          // Render PDF page
          const renderTask = page.render({
            canvasContext: context,
            viewport: viewport,
            canvas: canvas,
          });

          renderTasks.push(renderTask);
          await renderTask.promise;

          logger.info("✅ Rendered page", { pageNum });
        } catch (err) {
          // A zoom press supersedes whatever is drawing, so a cancellation here is the
          // mechanism working rather than a fault. Reporting it at error severity
          // filled telemetry with cancellations from ordinary zooming and buried the
          // genuine corruption this branch exists to surface.
          if (isRenderCancellation(err)) {
            logger.debug("Superseded page render cancelled", { pageNum });
          } else {
            logger.error(`Failed to render page ${pageNum}`, err, { pageNum, scale });
          }
        }
      }

      if (isMounted) setRenderVersion((version) => version + 1);
    };

    renderPages();

    return () => {
      isMounted = false;
      // Stop whatever is still drawing. `isMounted` only stops the loop advancing to
      // the next page, so without this a rapid series of zoom presses left overlapping
      // full-document passes running against canvases already detached from the
      // container, competing for the same worker.
      for (const task of renderTasks) task.cancel();
    };
  }, [pdfDocument, scale, hasMeasured]);

  // Scroll to matched page and add highlight (including cross-page plan).
  // Depends on `scale` because a zoom rebuilds every page, discarding the overlay
  // children this effect appended, so the highlights have to be drawn again, and on
  // `renderVersion` so that the redraw happens once those pages exist rather than
  // whenever the fixed delay below happens to expire.
  React.useEffect(() => {
    if (matchedPage === null || !containerRef.current) return;

    // Wait for pages to be rendered before scrolling. The delay is the fast path
    // for a short document; `renderVersion` is what guarantees a redraw on a long
    // one, where the pages do not exist yet when a fixed timer expires.
    //
    // A measured citation skips the timer, because its page is known before any
    // text is extracted and there is nothing to settle. The scroll still has to
    // wait for the page element to exist. On a short document the timer was the
    // dominant wait and dropping it is what makes the viewer open at the passage.
    // On a long one the element appears only when the sequential render pass
    // reaches it, and `renderVersion` ticks once at the end of that pass, so the
    // reader may still watch earlier pages fill before the jump lands.
    const delay = anchorPlanRef.current ? 0 : TEXT_MATCH_SETTLE_DELAY_MS;
    const scrollTimeout = setTimeout(() => {
      const container = containerRef.current;

      if (!container) return;

      // The steps, and the reasoning behind each scroll choice, are in
      // `./pdfCitationView`. Every ref is read when the timer fires rather than when
      // the effect runs. `overlayScale` is the overlay's own transform from the render
      // effect above, which the scroll needs to learn where a planned box is painted
      // on screen, so the two have to stay in step.
      applyCitationView({
        container,
        matchedPage,
        lastScrolledPageRef,
        measured: anchorPlanRef.current,
        scroller: scrollRef.current,
        overlayScale: scale / REFERENCE_SCALE,
        pageHighlights,
        highlightLayers: highlightRefs.current,
        citationText,
        textLayer,
      });
    }, delay);

    return () => clearTimeout(scrollTimeout);
  }, [matchedPage, citationText, textLayer, pageHighlights, scale, renderVersion]);

  // The caption is a sibling rather than a `label` prop. HeroUI v3's Spinner declares
  // only className, colour and size, so a `label` was forwarded to the underlying span
  // as a stray DOM attribute, rendering no text and leaving this status region with
  // nothing to announce.
  if (loading) {
    return (
      <div className="flex h-[60vh] flex-col items-center justify-center gap-3" role="status">
        <Spinner />
        <p className="text-sm text-default-500">{t("citations.pdfViewer.loading")}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-[60vh] items-center justify-center" role="alert">
        <p className="text-danger">{error}</p>
      </div>
    );
  }

  /** The controls stay inert until a scale has actually been settled on. */
  const zoomReady = hasMeasured && scale > 0;

  return (
    <div className="flex h-[75vh] w-full flex-col">
      <div ref={scrollRef} className="flex-1 overflow-auto bg-default-100 p-4">
        {/*
          `mx-auto w-fit` rather than `items-center`, which is what centres the pages
          while keeping a page wider than the container reachable. Centring by
          `align-items` puts the left overflow outside the scroll range, so the start
          of a wide page could not be scrolled to at all.
        */}
        <div ref={containerRef} className="mx-auto flex w-fit flex-col" />
      </div>

      <PDFViewerToolbar
        isFitToWidth={isFitToWidth}
        numPages={numPages}
        scale={scale}
        zoomReady={zoomReady}
        onFitToWidth={handleFitToWidth}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
      />
    </div>
  );
};

interface ZoomControlProps {
  label: string;
  dataId: string;
  icon: string;
  isDisabled: boolean;
  onPress: () => void;
}

/** One icon button in the toolbar, with its label repeated as the tooltip. */
const ZoomControl = ({ label, dataId, icon, isDisabled, onPress }: ZoomControlProps) => (
  <Tooltip>
    <TooltipTrigger>
      <Button
        isIconOnly
        aria-label={label}
        data-id={dataId}
        isDisabled={isDisabled}
        size="sm"
        variant="ghost"
        onPress={onPress}
      >
        <Icon icon={icon} width={16} />
      </Button>
    </TooltipTrigger>
    <TooltipContent>{label}</TooltipContent>
  </Tooltip>
);

interface ZoomReadoutProps {
  scale: number;
  zoomReady: boolean;
}

/**
 * The wording sits in visually hidden text rather than in an `aria-label`. A label
 * replaces the name computed from the content, so the region announced "Zoom level"
 * and never the percentage, which is the one thing it exists to report, and the
 * accessible name no longer contained the visible text (WCAG 2.5.3 Label in Name).
 */
const ZoomReadout = ({ scale, zoomReady }: ZoomReadoutProps) => {
  const { t } = useTranslation();

  return (
    <span
      aria-live="polite"
      className="min-w-[4ch] select-none text-center text-sm tabular-nums text-default-500"
      data-id="pdf-viewer-zoom-level"
      role="status"
    >
      <span className="sr-only">{t("common.zoom.level")}</span>
      <span data-id="pdf-viewer-zoom-value">
        {zoomReady ? `${Math.round(scale * ZOOM_PERCENTAGE_MULTIPLIER)}%` : null}
      </span>
    </span>
  );
};

interface PDFViewerToolbarProps {
  numPages: number;
  scale: number;
  /** False until a scale has actually been settled on, which keeps the controls inert. */
  zoomReady: boolean;
  isFitToWidth: boolean;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFitToWidth: () => void;
}

/** The page count and zoom controls beneath the pages. */
const PDFViewerToolbar = ({
  numPages,
  scale,
  zoomReady,
  isFitToWidth,
  onZoomIn,
  onZoomOut,
  onFitToWidth,
}: PDFViewerToolbarProps) => {
  const { t } = useTranslation();
  const atMaximumZoom = zoomReady && scale >= MAX_SCALE;
  const atMinimumZoom = zoomReady && scale <= MIN_SCALE;

  // A disabled control keeps its tooltip, so the tooltip is where the reason for the
  // disabling belongs. Saying "Zoom in" on a button that will not zoom in tells the
  // user nothing about why, which is the pattern ChatModelSelector and RegenerateButton
  // already avoid. The plain label is retained while the control is merely not ready,
  // since that state is transient and has no reason worth naming.
  const zoomInLabel = atMaximumZoom ? t("common.zoom.atMaximum") : t("common.zoom.in");
  const zoomOutLabel = atMinimumZoom ? t("common.zoom.atMinimum") : t("common.zoom.out");
  const fitToWidthLabel = isFitToWidth
    ? t("citations.pdfViewer.alreadyFitted")
    : t("citations.pdfViewer.fitToWidth");

  return (
    <div
      className="flex shrink-0 items-center justify-between gap-2 border-t border-default-200 bg-content1 px-3 py-2"
      data-id="pdf-viewer-toolbar"
    >
      <span className="text-sm text-default-500" data-id="pdf-viewer-page-count">
        {numPages > 0 ? t("common.pageCount", { count: numPages }) : null}
      </span>

      <div className="flex items-center gap-1">
        <ZoomControl
          dataId="pdf-viewer-zoom-out"
          icon="solar:magnifer-zoom-out-linear"
          isDisabled={!zoomReady || scale <= MIN_SCALE}
          label={zoomOutLabel}
          onPress={onZoomOut}
        />

        <ZoomReadout scale={scale} zoomReady={zoomReady} />

        <ZoomControl
          dataId="pdf-viewer-zoom-in"
          icon="solar:magnifer-zoom-in-linear"
          isDisabled={!zoomReady || scale >= MAX_SCALE}
          label={zoomInLabel}
          onPress={onZoomIn}
        />

        <div className="mx-0.5 h-4 w-px bg-default-200" />

        <ZoomControl
          dataId="pdf-viewer-fit-to-width"
          icon="solar:align-horizontal-spacing-linear"
          isDisabled={isFitToWidth}
          label={fitToWidthLabel}
          onPress={onFitToWidth}
        />
      </div>
    </div>
  );
};

export default PDFViewer;
